import { apiClient } from '../../apiHelper';
import {
  useCreate,
  useEdit,
  usePaginated,
  useSingleFetch,
  usePost_old,
  useQuery,
  useFetch,
} from '../react-query';
import { patientsQueryKeys } from './patients-query-keys';

import { Patient } from '../../../UI/Sections/PatientsList/patientsList.section.custom';
import * as url from '../../urlHelper';
import { AxiosResponse } from 'axios';

interface Filters {
  [key: string]: string | number;
}

interface PatientData {
  patient_id: string | number;
  values: Record<string, unknown>;
}

interface PermissionData {
  patient_id: string | number;
  user_uid: string;
  permission: string;
}

const patientListInvalidationKeys = [
  patientsQueryKeys.all.map(String),
  ['recentPatients'],
  ['paginatedPatient'],
];

// interface ApiResponse<T = any> {
//   data: T;
// }

// interface InputFilters {
//   global__ilike?: string;
//   [key: string]: any;
// }

// interface SanitizedFilters extends Omit<InputFilters, 'global__ilike'> {
//   condition__or?: {
//     patient_name__ilike: string;
//     patient_last_name__ilike: string;
//     email__ilike: string;
//   };
// }

// export function usePaginatedPatients(
//   page: number,
//   itemsPerPage: number,
//   filters: InputFilters = {},
//   config: QueryConfig = {}
// ) {
//   const getPaginatedPatientsFn = async (p = page, f = filters) => {
//     const prepareFilters = (inputFilters: InputFilters): SanitizedFilters => {
//       console.log('Input filters:', inputFilters);
//       if (!inputFilters) {
//         return {};
//       }

//       if (!inputFilters.global__ilike || inputFilters.global__ilike.length === 0) return {};

//       const sanitizedFilters: SanitizedFilters = { ...inputFilters };

//       if (sanitizedFilters.global__ilike) {
//         sanitizedFilters['condition__or'] = {
//           patient_name__ilike: `%${sanitizedFilters.global__ilike}%`,
//           patient_last_name__ilike: `%${sanitizedFilters.global__ilike}%`,
//           email__ilike: `%${sanitizedFilters.global__ilike}%`,
//         };

//         delete sanitizedFilters.global__ilike;

//       }

//       return sanitizedFilters;
//     };

//     const sanitizedFilters = prepareFilters(f);
//     const queryString = `page=${p}&items_per_page=${itemsPerPage}&filters=${encodeURIComponent(
//       JSON.stringify(sanitizedFilters)
//     )}`;

//     const patientUrl = `patients?${queryString}`;

//     const response = await apiClient.get(patientUrl);
//     return response;
//   };

//   return usePaginated(
//     getPaginatedPatientsFn,
//     patientsQueryKeys.pagination(page, filters),
//     page,
//     config
//   );
// }

export function usePaginatedPatients(
  page: number,
  itemsPerPage: number,
  filters: Filters = {},
  config = {}
) {
  const getPaginatedPatientsFn = async (p = page, f = filters) => {
    const filtersQuery = Object.keys(f)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(f[key])}`)
      .join('&');
    const url = `patients?page=${p}&items_per_page=${itemsPerPage}${
      filtersQuery ? `&${filtersQuery}` : ''
    }`;

    const response = await apiClient.get(url);
    return response.data;
  };
  return usePaginated(
    getPaginatedPatientsFn,
    ['paginatedPatient', ...patientsQueryKeys.pagination(page).map(String)],
    // patientsQueryKeys.pagination(page).map(String),
    page,
    config
  );
}

export function useRecentPatients(global_search: string = '', config = {}) {
  const getRecentPatientsFn = async () => {
    const response = await apiClient.get(
      `recent-patients?global_search=${global_search}`
    );
    return response.data;
  };
  return useQuery(
    getRecentPatientsFn,
    // patientsQueryKeys.recent().map(String),
    ['recentPatients'],
    {
      ...config,
    }
  );
}

export function useSinglePatient(patientId: number | string, config = {}) {
  const fetchPatient = async () => {
    if (patientId === -1) {
      return null;
    }
    return await apiClient.get(`patients/${patientId}`);
  };

  return useSingleFetch(
    fetchPatient,
    patientsQueryKeys.detail(patientId).map(String),
    patientId,
    config
  );
}

export function useSinglePatientWithRelated(
  patient_id: number | string,
  config = {}
) {
  const getPatientFn = async () => {
    if (patient_id === -1) {
      return null;
    }
    // Legacy name kept for callers; `/with_related` was replaced by dossier.
    const response = await apiClient.get(`patients/${patient_id}/dossier`);
    return response;
  };

  return useSingleFetch(
    getPatientFn,
    patientsQueryKeys.detailWithRelated(patient_id).map(String),
    patient_id,
    config
  );
}
export function usePermissionsUserPatient(
  patient_id: number | string,
  user_uid: string,
  config = {}
) {
  const getPatientFn = async () => {
    const response = await apiClient.get(
      `patients/${patient_id}/get_permissions/${user_uid}`
    );
    return response;
  };

  return useSingleFetch(
    getPatientFn,
    patientsQueryKeys.permissions(patient_id, user_uid).map(String),
    null,
    config
  );
}

export function useEditPatient(
  onSuccess?: (response: AxiosResponse, data: Patient) => void,
  onError?: (responce: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const editPatientFn = async (data: PatientData) => {
    const { patient_id, values } = data;
    const response = await apiClient.put(`patients/${patient_id}`, values);
    return response;
  };

  return useEdit(
    editPatientFn,
    patientsQueryKeys.all.map(String),
    'Error while Editing patient',
    'Patient edited successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useTakeOwnership() {
  const takeOwnershipFn = async (patient_id: PermissionData) => {
    const response = await apiClient.post(
      `patients/${patient_id}/take_ownership`
    );
    return response;
  };

  return useEdit(
    takeOwnershipFn,
    patientsQueryKeys.all.map(String),
    'Error while taking ownership',
    'Ownership taken successfully'
  );
}

export function useGrantPermission() {
  const grantPermissionFn = async (data: PermissionData) => {
    const { patient_id, user_uid, permission } = data;
    const response = await apiClient.post(
      `patients/${patient_id}/grant_permission/${user_uid}/${permission}`
    );
    return response;
  };

  return useEdit(
    grantPermissionFn,
    patientsQueryKeys.all.map(String),
    'Error while granting Permission',
    'Permission granted successfully'
  );
}

export function useRevokePermission() {
  const revokePermissionFn = async (data: PermissionData) => {
    const { patient_id, user_uid, permission } = data;
    const response = await apiClient.post(
      `patients/${patient_id}/revoke_permission/${user_uid}/${permission}`
    );
    return response;
  };

  return useEdit(
    revokePermissionFn,
    patientsQueryKeys.all.map(String),
    'Error while revoking Permission',
    'Permission revoked successfully'
  );
}

export function useArchivePatient() {
  const archivePatientFn = async (patient_id: string | number) => {
    const response = await apiClient.post(`patients/archive/${patient_id}`);
    return response;
  };

  return useEdit(
    archivePatientFn,
    patientsQueryKeys.all.map(String),
    'Error while archiving patient',
    'Patient archived successfully'
  );
}

export function useUnarchivePatient() {
  const unarchivePatientFn = async (patient_id: string | number) => {
    const response = await apiClient.post(`patients/unarchive/${patient_id}`);
    return response;
  };

  return useEdit(
    unarchivePatientFn,
    patientsQueryKeys.all.map(String),
    'Error while unarchiving patient',
    'Patient unarchived successfully'
  );
}

export function useArchivePatients() {
  const archivePatientsFn = async (patient_ids: (string | number)[]) => {
    const response = await apiClient.post(`patients/archive`, patient_ids);
    return response;
  };

  return useEdit(
    archivePatientsFn,
    patientsQueryKeys.all.map(String),
    'Error while archiving patients',
    'Patients archived successfully'
  );
}

export function useUnarchivePatients() {
  const unarchivePatientsFn = async (patient_ids: (string | number)[]) => {
    const response = await apiClient.post(`patients/unarchive`, patient_ids);
    return response;
  };

  return useEdit(
    unarchivePatientsFn,
    patientsQueryKeys.all.map(String),
    'Error while unarchiving patients',
    'Patients unarchived successfully'
  );
}

export function useDeletePatient() {
  const deletePatientFn = async (patient_id: string | number) => {
    // Frontend calls the backend DELETE /patients/{patient_id} endpoint
    const response = await apiClient.delete(`patients/${patient_id}`);
    return response;
  };

  return useEdit(
    deletePatientFn,
    patientsQueryKeys.all.map(String),
    'Error while deleting patient',
    'Patient deleted successfully'
  );
}

export function useCreatePatient(
  onSuccess?: (response: AxiosResponse, data: Patient) => void,
  onError?: (responce: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const createPatientFn = async (data: Patient) => {
    const response = await apiClient.post(`patients`, data);
    return response;
  };

  return useCreate(
    createPatientFn,
    patientListInvalidationKeys,
    'Error while creating patient',
    'Patient created successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useCreatePatientWithPregnancy(
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (error: unknown) => void
) {
  const createPatientWithPregnancyFn = async (data: {
    patient: Record<string, unknown>;
    pregnancy: Record<string, unknown>;
  }) => {
    const response = await apiClient.post(`patients/with-pregnancy`, data);
    return response;
  };

  return useCreate(
    createPatientWithPregnancyFn,
    patientListInvalidationKeys,
    'Error while creating patient with pregnancy',
    'Patient and pregnancy created successfully',
    onSuccess,
    onError,
    true
  );
}

export interface PatientDossierFamilyHistoryItem {
  condition_code:
    | 'HTA'
    | 'DIABETES'
    | 'HEREDITARY_DISEASE'
    | 'MALFORMATION'
    | 'ALLERGY'
    | 'OTHER';
  wife_family_notes?: string | null;
  husband_family_notes?: string | null;
}

export type ObstetricAnomalyType =
  | 'AVORTEMENT'
  | 'PREMATURE'
  | 'IUFD'
  | 'CONGENITAL'
  | 'OTHER';

export type DeliveryType = 'NORMAL' | 'CESAREAN' | 'INSTRUMENTAL' | 'OTHER';

export type NewbornSex = 'M' | 'F' | 'UNKNOWN';

export type NewbornVitalStatus = 'ALIVE' | 'DECEASED_NEONATAL' | 'STILLBIRTH';

export type PregnancyTermStatus = 'PRETERM' | 'TERM' | 'POSTTERM';

export interface PatientDossierPriorDelivery {
  id?: number | null;
  delivery_index: number;
  delivery_date?: string | null;
  delivery_type?: DeliveryType | null;
  delivery_ga_weeks?: number | null;
  delivery_ga_days?: number | null;
  pregnancy_term_status?: PregnancyTermStatus | null;
  extraction_modalities?: string | null;
  cesarean_indication?: string | null;
  delivery_place?: string | null;
  delivery_complication_type?: string | null;
  newborn_complication_type?: string | null;
  newborn_weight_grams?: number | null;
  newborn_weights_note?: string | null;
  newborn_sex?: NewbornSex | null;
  newborn_vital_status?: NewbornVitalStatus | null;
  has_obstetric_anomalies?: boolean;
  anomaly_type?: ObstetricAnomalyType | null;
  anomaly_count?: number | null;
  anomaly_date?: string | null;
  anomaly_place?: string | null;
  anomaly_ga_weeks?: number | null;
  anomaly_description?: string | null;
}

export interface PatientDossierPayload extends Record<string, unknown> {
  patient: Record<string, unknown>;
  pregnancy?: Record<string, unknown> | null;
  family_history?: PatientDossierFamilyHistoryItem[];
  patient_medical_history?: Record<string, unknown> | null;
  prior_deliveries?: PatientDossierPriorDelivery[];
}

export interface PatientDossierSectionError {
  section: string;
  message: string;
}

export interface PatientDossierSectionResult {
  section: string;
  status: 'saved' | 'failed';
  message?: string;
}

export interface PatientDossierResponseData {
  patient: Patient;
  pregnancy?: Record<string, unknown> | null;
  family_history?: Record<string, unknown>[] | null;
  patient_medical_history?: Record<string, unknown> | null;
  prior_deliveries?: Record<string, unknown>[] | null;
  errors?: PatientDossierSectionError[];
  section_results?: PatientDossierSectionResult[];
  patient_id?: string;
}

export type PatientDossierUpdatePayload = {
  patient_id: string | number;
} & Record<string, unknown>;

export function useCreatePatientDossier(
  onSuccess?: (response: AxiosResponse, data: PatientDossierPayload) => void,
  onError?: (error: unknown) => void,
  notification: boolean = true
) {
  const createDossierFn = async (data: PatientDossierPayload) => {
    const response = await apiClient.post(`patients/dossier`, data);
    return response;
  };

  return useCreate<PatientDossierPayload>(
    createDossierFn,
    patientListInvalidationKeys,
    'Error while creating patient dossier',
    'Patient dossier created successfully',
    onSuccess,
    onError,
    notification
  );
}

// for rabi, i ll migrate it soon
export const useAddPatient = (
  updater: (oldData: Patient[], newData: Patient) => Patient[],
  onSuccess?: (response: unknown, data: Patient) => void,
  onError?: (error: Error, data: Patient) => void
) =>
  usePost_old<Patient[], Patient>(
    url.POST_PATIENT,
    undefined,
    updater,
    onSuccess,
    onError
  );

export const fetchFamilyHistory = async (patient_id: string) => {
  const response = await apiClient.get(`patients/${patient_id}/family-history`);
  return response.data;
};

export const updateFamilyHistory = async (
  patient_id: string,
  data: Record<string, unknown>
) => {
  const response = await apiClient.put(
    `patients/${patient_id}/family-history`,
    data
  );
  return response.data;
};

export const fetchDeliveryFile = async (patient_id: string) => {
  const response = await apiClient.get(`patients/${patient_id}/delivery-file`);
  return response.data;
};

export const updateDeliveryFile = async (
  patient_id: string,
  data: Record<string, unknown>
) => {
  const response = await apiClient.put(
    `patients/${patient_id}/delivery-file`,
    data
  );
  return response.data;
};

export function usePatientFamilyHistory(
  patient_id: string | number | undefined,
  config: Record<string, unknown> = {}
) {
  return useFetch<unknown>(
    async () => {
      if (!patient_id) {
        return null;
      }
      const response = await apiClient.get(
        `patients/${patient_id}/family-history`
      );
      return response.data;
    },
    patient_id ? ['patients', String(patient_id), 'family-history'] : null,
    { enabled: Boolean(patient_id), ...config }
  );
}

export function usePriorDeliveries(
  patient_id: string | number | undefined,
  config: Record<string, unknown> = {}
) {
  return useFetch<unknown>(
    async () => {
      if (!patient_id) {
        return null;
      }
      const response = await apiClient.get(
        `patients/${patient_id}/prior-deliveries`
      );
      return response.data;
    },
    patient_id ? ['patients', String(patient_id), 'prior-deliveries'] : null,
    { enabled: Boolean(patient_id), ...config }
  );
}

export function usePatientDossier(
  patient_id: string | number | undefined,
  config: Record<string, unknown> = {}
) {
  return useFetch<unknown>(
    async () => {
      if (!patient_id) {
        return null;
      }
      const response = await apiClient.get(`patients/${patient_id}/dossier`);
      return response.data;
    },
    patient_id ? patientsQueryKeys.dossier(patient_id).map(String) : null,
    { enabled: Boolean(patient_id), ...config }
  );
}

export function useUpdatePatientDossier(
  onSuccess?: (
    response: AxiosResponse,
    data: PatientDossierUpdatePayload
  ) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const updateDossier = async (data: PatientDossierUpdatePayload) => {
    const { patient_id, ...body } = data;
    const response = await apiClient.put(
      `patients/${patient_id}/dossier`,
      body
    );
    return response;
  };

  return useEdit<PatientDossierUpdatePayload>(
    updateDossier,
    [...patientsQueryKeys.all.map(String)],
    'Failed to update patient dossier',
    'Patient dossier updated successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useCreatePriorDelivery(
  onSuccess?: (response: AxiosResponse) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const createPriorDelivery = async (
    data: { patient_id: string | number } & Record<string, unknown>
  ) => {
    const { patient_id, ...body } = data;
    const response = await apiClient.post(
      `patients/${patient_id}/prior-deliveries`,
      body
    );
    return response;
  };

  return useCreate(
    createPriorDelivery,
    [...patientsQueryKeys.all.map(String)],
    'Failed to record delivery',
    'Delivery recorded successfully',
    onSuccess,
    onError,
    notification
  );
}
